// Modal for selecting activities by WBS navigation only
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Modal, Spin, Button, Typography, Divider, Empty, Table, Input, InputNumber, Space } from 'antd';
import { useGetPrimaveraTasksSmall } from '../../hooks/genericHooks';
import type { PrimaveraTaskSmall } from '../../types';
import WbsSelectModal from './WbsSelectModal';

const WIDTH_STORAGE_KEY = 'ActivitySelectByWbsModal:width';
const DEFAULT_WIDTH = 650;
const MIN_WIDTH = 520;
const MAX_WIDTH = 1400;

interface ActivitySelectByWbsModalProps {
  open: boolean;
  onOk: (result: { wbs: string | null, activities: PrimaveraTaskSmall[] }) => void;
  onCancel: () => void;
  filters?: { [key: string]: any };
}

// Content-only version for embedding in other modals
export const ActivitySelectByWbsContent: React.FC<Omit<ActivitySelectByWbsModalProps, 'open' | 'onOk' | 'onCancel'> & {
  selectedWbsId: string | null;
  setSelectedWbsId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedActivityIds: string[];
  setSelectedActivityIds: React.Dispatch<React.SetStateAction<string[]>>;
  wbsModalOpen: boolean;
  setWbsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalWidth: number;
  setModalWidth: React.Dispatch<React.SetStateAction<number>>;
}> = ({
  filters,
  selectedWbsId,
  setSelectedWbsId,
  selectedActivityIds,
  setSelectedActivityIds,
  wbsModalOpen,
  setWbsModalOpen,
  modalWidth,
  setModalWidth,
}) => {
  const { data: allTasks, isLoading: isLoadingTasks } = useGetPrimaveraTasksSmall();

  const isLoading = isLoadingTasks;
  const hasNoData = !allTasks?.data?.length;

  const activities = useMemo(() => {
    if (!selectedWbsId || !Array.isArray(allTasks?.data)) return [] as PrimaveraTaskSmall[];
    const wbsId = String(selectedWbsId);
    return (allTasks!.data as PrimaveraTaskSmall[]).filter(a => String(a.wbs_id ?? '') === wbsId);
  }, [selectedWbsId, allTasks]);

  // Activity search (multi-word AND, order-agnostic)
  const [activitySearch, setActivitySearch] = useState('');
  const filteredActivities = useMemo(() => {
    if (!activitySearch) return activities;
    const tokens = activitySearch.toLowerCase().split(/\s+/).filter(Boolean);
    return activities.filter(a => {
      const haystack = `${a.task_code ?? ''} ${a.task_name ?? ''} ${a.wbs_id ?? ''} ${a.task_id ?? ''}`.toLowerCase();
      return tokens.every(t => haystack.includes(t));
    });
  }, [activitySearch, activities]);

  const formatDate = (value: any): string => {
    if (!value) return '';
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch (e) {
      // ignore
    }
    return s.slice(0, 10);
  };

  if (isLoading) {
    return <Spin tip="Loading WBS and activities..." style={{ width: '100%', margin: '60px 0' }} />;
  }
  if (hasNoData) {
    return <Empty description="No data available" style={{ width: '100%', margin: '60px 0' }} />;
  }

  const handleClearSelection = () => {
    setSelectedWbsId(null);
    setSelectedActivityIds([]);
  };
  const handleActivitySelect = (id: string | number) => {
    setSelectedActivityIds(prev => {
      const idStr = String(id);
      const updated = prev.includes(idStr) ? prev.filter(x => x !== idStr) : [...prev, idStr];
      return updated;
    });
  };
  const openWbsModal = () => setWbsModalOpen(true);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Space size={8} align="center">
          <Typography.Text type="secondary">Width</Typography.Text>
          <InputNumber
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            step={20}
            value={modalWidth}
            onChange={v => {
              const n = typeof v === 'number' ? v : DEFAULT_WIDTH;
              setModalWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n)));
            }}
            style={{ width: 110 }}
          />
          <Button
            size="small"
            onClick={() => setModalWidth(DEFAULT_WIDTH)}
            disabled={modalWidth === DEFAULT_WIDTH}
          >
            Reset
          </Button>
        </Space>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0 }}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Select Activities by WBS
        </Typography.Title>
        <Button size="small" style={{ marginLeft: 12 }} onClick={handleClearSelection}>
          Clear
        </Button>
      </div>
      <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
        Choose a WBS node that has activities, then select activities below.
      </Typography.Text>
      <Divider style={{ margin: '6px 0' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <Button onClick={openWbsModal}>Select WBS</Button>
        {selectedWbsId && (
          <Typography.Text type="secondary">Selected WBS ID: {selectedWbsId}</Typography.Text>
        )}
      </div>
      <WbsSelectModal
        open={wbsModalOpen}
        onOk={wbs => { setSelectedWbsId(String(wbs.wbs_id)); setWbsModalOpen(false); setSelectedActivityIds([]); }}
        onCancel={() => setWbsModalOpen(false)}
      />
      {selectedWbsId && activities.length > 0 && (
        <div style={{ margin: '8px 0' }}>
          <Typography.Title level={5}>Select Activities</Typography.Title>
          <Input.Search
            placeholder="Search activities..."
            allowClear
            value={activitySearch}
            onChange={e => setActivitySearch(e.target.value)}
            style={{ margin: '4px 0 4px' }}
          />
          <Table
            columns={[
              {
                title: '',
                dataIndex: 'task_id',
                key: 'task_id',
                width: 48,
                align: 'center' as const,
                render: (id: string | number) => (
                  <Input
                    type="checkbox"
                    checked={selectedActivityIds.includes(String(id))}
                    onChange={() => handleActivitySelect(id)}
                  />
                )
              },
              {
                title: 'Act.ID',
                dataIndex: 'task_id',
                key: 'task_id',
                width: 70,
              },
              {
                title: 'Name',
                dataIndex: 'task_name',
                key: 'task_name',
                ellipsis: true,
              },
              {
                title: 'Start',
                dataIndex: 'target_start_date',
                key: 'target_start_date',
                width: 110,
                render: (v: any) => formatDate(v),
              },
              {
                title: 'Finish',
                dataIndex: 'target_end_date',
                key: 'target_end_date',
                width: 110,
                render: (v: any) => formatDate(v),
              },
            ]}
            dataSource={filteredActivities}
            pagination={false}
            rowKey="task_id"
            size="small"
            // scroll={{ x: true }}
          />
        </div>
      )}
    </>
  );
};

const ActivitySelectByWbsModal: React.FC<ActivitySelectByWbsModalProps> = ({ open, onOk, onCancel, filters }) => {
  const { data: allTasks } = useGetPrimaveraTasksSmall();
  const [selectedWbsId, setSelectedWbsId] = useState<string | null>(null);
  const [wbsModalOpen, setWbsModalOpen] = useState(false);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [modalWidth, setModalWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed)) {
        return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed));
      }
    } catch {
      // ignore
    }
    return DEFAULT_WIDTH;
  });

  useEffect(() => {
    if (open) setSelectedActivityIds([]);
  }, [open]);
  useEffect(() => { setSelectedActivityIds([]); }, [selectedWbsId]);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(modalWidth));
    } catch {
      // ignore
    }
  }, [modalWidth]);

  return (
    <Modal
      open={open}
      onOk={() => {
        const activities = selectedWbsId && Array.isArray(allTasks?.data)
          ? (allTasks!.data as PrimaveraTaskSmall[])
              .filter(a => String(a.wbs_id ?? '') === String(selectedWbsId))
              .filter(a => selectedActivityIds.includes(String(a.task_id)))
          : [];
        onOk({
          wbs: selectedWbsId,
          activities,
        });
      }}
      onCancel={onCancel}
      okButtonProps={{
        disabled: !selectedWbsId || selectedActivityIds.length === 0
      }}
      title={null}
      width={modalWidth}
      styles={{ body: { minHeight: '70vh', maxHeight: '80vh', overflowY: 'auto' } }}
      footer={undefined}
    >
      <ActivitySelectByWbsContent
        filters={filters}
        selectedWbsId={selectedWbsId}
        setSelectedWbsId={setSelectedWbsId}
        selectedActivityIds={selectedActivityIds}
        setSelectedActivityIds={setSelectedActivityIds}
        wbsModalOpen={wbsModalOpen}
        setWbsModalOpen={setWbsModalOpen}
        modalWidth={modalWidth}
        setModalWidth={setModalWidth}
      />
    </Modal>
  );
};

export default ActivitySelectByWbsModal;
